import { useAppDispatch } from 'app/hooks';
import { useGetCompaniesQuery } from 'features/company/services';
import { useNavigate, useParams } from 'react-router-dom';
import { WEBSITE_FIELDS_SCHEMA, WEBSITE_INITIAL_FIELDS } from '../constants/website-fields';
import { FETCH_ALL_PARAMS } from 'constants/query';
import { useForm } from 'hooks/useForm';
import { useEditWebsiteMutation, useGetWebsiteQuery } from '../services';
import { useRole } from 'hooks/useRole';
import { FormEvent, useCallback, useEffect } from 'react';
import { setErrorMessage, setSuccessMessage } from 'store/toast-slice';
import { ServerErrorType } from 'types/server-responses';
import { trimValues } from 'utils/object-helpers';
import { Website } from '../types/dtos';

const useController = () => {
    const { websiteIdEdit } = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [editWebsite, { isLoading: isEditWebsiteLoading }] = useEditWebsiteMutation();
    const { data: website } = useGetWebsiteQuery(websiteIdEdit as string);
    const { isAdmin } = useRole();
    const { data: companies, isLoading: isGetCompaniesLoading } = useGetCompaniesQuery(FETCH_ALL_PARAMS, {
        skip: !isAdmin,
    });
    const { formData, setInitialFormData, handleChange, handleSubmit, errors, isDirty, changedValues } = useForm({
        validationSchema: WEBSITE_FIELDS_SCHEMA,
        options: {
            trackChangedValues: true,
        },
    });

    useEffect(() => {
        if (website) setInitialFormData({ ...WEBSITE_INITIAL_FIELDS, ...website });
    }, [website, setInitialFormData]);

    const handleSuccess = useCallback(() => {
        dispatch(setSuccessMessage({ key: 'website.WEBSITE_EDITED' }));
    }, [dispatch]);

    const handleErrors = useCallback(
        (error: ServerErrorType) => {
            const code = error.data?.errorCode;
            dispatch(setErrorMessage({ key: code }));
        },
        [dispatch],
    );

    const getPayload = useCallback(() => {
        const data = { ...changedValues };
        return trimValues(data);
    }, [changedValues]);

    const onSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            handleSubmit(async () => {
                try {
                    const payload = getPayload();
                    await editWebsite({ id: websiteIdEdit as string, payload }).unwrap();

                    handleSuccess();

                    navigate(-1);
                } catch (error) {
                    handleErrors(error as ServerErrorType);
                }
            });
        },
        [editWebsite, getPayload, handleErrors, handleSubmit, handleSuccess, websiteIdEdit, navigate],
    );

    const onCancel = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    return {
        formData: formData as Website,
        companies,
        onChange: handleChange,
        onSubmit,
        onCancel,
        dataChanged: isDirty,
        isLoading: formData === undefined || isGetCompaniesLoading,
        isEditWebsiteLoading: isEditWebsiteLoading,
        errors,
        isAdmin,
    };
};

export default useController;
